import type { KategoriaFilter } from "@/api/queryKeys";
import type { StavZakazky } from "@/api/schemas";

/**
 * Filter prehľadu zákaziek v adrese (KOLO 171).
 *
 * Do KOLA 171 bol stav, hľadanie, triedenie aj strana len v stave stránky —
 * po návrate zo zákazky bol zoznam vždy na Všetkých a s prázdnym hľadaním.
 * Teraz sú v adrese (`/zakazky?stav=ponuka&hladat=…`), takže ich drží šípka
 * Späť prehliadača, a posledný filter si pamätá karta prehliadača
 * (`sessionStorage`) pre šípku v hlavičke zákazky a položku Zákazky v menu.
 *
 * Predvolené hodnoty sa do adresy nepíšu — čistý zoznam je `/zakazky`.
 *
 * Od KOLA 172 aj kategória (`?kategoria=<id>` alebo `?kategoria=bez`).
 */

export type ZalozkaZoznamu = "vsetky" | StavZakazky | "archiv";
export type PoleTriedenia = "cislo_zakazky" | "nazov" | "stav" | "updated_at";

/** Kategória v ľavom paneli: null = Všetky, `"bez"` = Bez kategórie, inak id. */
export type KategoriaZoznamu = string | null;
export const BEZ_KATEGORIE = "bez";

export interface FiltreZoznamu {
  zalozka: ZalozkaZoznamu;
  kategoria: KategoriaZoznamu;
  hladat: string;
  triedit: PoleTriedenia;
  zostupne: boolean;
  strana: number;
}

export const PREDVOLENE_FILTRE: FiltreZoznamu = {
  zalozka: "vsetky",
  kategoria: null,
  hladat: "",
  triedit: "updated_at",
  zostupne: true,
  strana: 1,
};

const ZALOZKY: readonly ZalozkaZoznamu[] = [
  "vsetky",
  "ponuka",
  "v_priprave",
  "vo_vyrobe",
  "dokoncena",
  "archiv",
];
const POLIA: readonly PoleTriedenia[] = [
  "cislo_zakazky",
  "nazov",
  "stav",
  "updated_at",
];

/** Parametre adresy, ktoré patria zoznamu — cudzie (`koncept`…) ostanú. */
const PARAMETRE = ["stav", "kategoria", "hladat", "triedit", "strana"] as const;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function kategoriaZAdresy(hodnota: string | null): KategoriaZoznamu {
  if (hodnota === BEZ_KATEGORIE) return BEZ_KATEGORIE;
  return hodnota && UUID.test(hodnota) ? hodnota : null;
}

/** Filtre z adresy; neznáma alebo chýbajúca hodnota je predvolená. */
export function filtreZAdresy(params: URLSearchParams): FiltreZoznamu {
  const stav = params.get("stav");
  const triedit = params.get("triedit") ?? "";
  const pole = POLIA.find((p) => p === triedit.replace(/^-/, ""));
  const strana = Number(params.get("strana"));
  return {
    zalozka: ZALOZKY.find((z) => z === stav) ?? PREDVOLENE_FILTRE.zalozka,
    kategoria: kategoriaZAdresy(params.get("kategoria")),
    hladat: params.get("hladat") ?? "",
    triedit: pole ?? PREDVOLENE_FILTRE.triedit,
    zostupne: pole ? triedit.startsWith("-") : PREDVOLENE_FILTRE.zostupne,
    strana: Number.isInteger(strana) && strana > 1 ? strana : 1,
  };
}

/** Kópia parametrov s filtrami — predvolené zmaže, cudzie nechá. */
export function filtreDoAdresy(
  filtre: FiltreZoznamu,
  params: URLSearchParams,
): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const kluc of PARAMETRE) next.delete(kluc);
  if (filtre.zalozka !== PREDVOLENE_FILTRE.zalozka) {
    next.set("stav", filtre.zalozka);
  }
  if (filtre.kategoria) next.set("kategoria", filtre.kategoria);
  // Hľadanie ide do adresy, ako je napísané — orezaním by pole pri
  // písaní medzery skákalo; samé medzery sú prázdne hľadanie
  if (filtre.hladat.trim()) next.set("hladat", filtre.hladat);
  const triedenie = triedenieApi(filtre);
  if (triedenie !== triedenieApi(PREDVOLENE_FILTRE)) {
    next.set("triedit", triedenie);
  }
  if (filtre.strana > 1) next.set("strana", String(filtre.strana));
  return next;
}

/** Kategória pre `GET /zakazky` a `stav-pocty`. */
export function kategoriaApi(filtre: FiltreZoznamu): KategoriaFilter {
  if (filtre.kategoria === BEZ_KATEGORIE) return { bez_kategorie: true };
  return filtre.kategoria ? { kategoria_id: filtre.kategoria } : {};
}

/** Triedenie pre `GET /zakazky` (`-updated_at` = zostupne). */
export function triedenieApi(filtre: FiltreZoznamu): string {
  return `${filtre.zostupne ? "-" : ""}${filtre.triedit}`;
}

const KLUC_ADRESY = "tandem.zakazky.filter";

/** Zapamätá filter pre túto kartu prehliadača — len parametre zoznamu. */
export function zapamatajFiltre(filtre: FiltreZoznamu): void {
  const search = filtreDoAdresy(filtre, new URLSearchParams()).toString();
  try {
    if (search) window.sessionStorage.setItem(KLUC_ADRESY, search);
    else window.sessionStorage.removeItem(KLUC_ADRESY);
  } catch {
    // Zablokované úložisko — zoznam sa len nevráti na posledný filter
  }
}

/** Adresa zoznamu s posledným filtrom tejto karty prehliadača. */
export function adresaZoznamu(): string {
  try {
    const search = window.sessionStorage.getItem(KLUC_ADRESY);
    return search ? `/zakazky?${search}` : "/zakazky";
  } catch {
    return "/zakazky";
  }
}

/**
 * Zákazky v menu sa vrátia do otvorenej zákazky (KOLO 215). User: *„keď
 * idem do Cenníka a vrátim sa do zákazky, tak bude otvorená, nie že ju
 * musím znova otvoriť"*. Pamätá sa posledné miesto v Zákazkách — editor
 * zákazku zapíše, zoznam ju zmaže. Na okno (`sessionStorage`) ako filter,
 * takže dve okná na rôznych zákazkách si neprekážajú.
 */
const KLUC_ZAKAZKY = "tandem.zakazky.otvorena";

export function zapamatajZakazku(id: string): void {
  try {
    window.sessionStorage.setItem(KLUC_ZAKAZKY, id);
  } catch {
    // Zablokované úložisko — menu vedie na zoznam ako doteraz
  }
}

export function zabudniZakazku(): void {
  try {
    window.sessionStorage.removeItem(KLUC_ZAKAZKY);
  } catch {
    // nič
  }
}

/**
 * Kam vedú Zákazky v menu: mimo Zákaziek do naposledy otvorenej zákazky
 * (ak user z nej odišiel), inak na zoznam s posledným filtrom — aj
 * z editora, ako doteraz.
 */
export function adresaZakaziek(pathname: string): string {
  if (!pathname.startsWith("/zakazky")) {
    try {
      const id = window.sessionStorage.getItem(KLUC_ZAKAZKY);
      if (id) return `/zakazky/${id}`;
    } catch {
      // zoznam
    }
  }
  return adresaZoznamu();
}
